import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useStudio } from "./StudioContext";
import { StudioPage, Panel, SectionLabel, Metric, DataTable, Loading, Badge, cx } from "./StudioShared";
import { Radio, ShieldCheck, Gauge, GitBranch, Activity, Loader2, KeyRound } from "lucide-react";

const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

// Health taxonomy → tone + label
const HEALTH = {
  healthy: ["green", "Healthy"],
  unavailable: ["amber", "Unavailable"],
  rate_limited: ["amber", "Rate Limited"],
  auth_failed: ["red", "Auth Failed"],
  missing_api_key: ["slate", "No API Key"],
  dormant: ["slate", "Dormant"],
  unknown: ["slate", "Unknown"],
};

export default function Providers() {
  const { providers, requests, reload } = useStudio();
  const [checking, setChecking] = useState(false);
  if (!providers) return <StudioPage title="Provider Dashboard" question="Who is doing the reasoning?"><Loading /></StudioPage>;
  const list = providers.providers || [];

  const runHealthCheck = async () => {
    setChecking(true);
    try {
      await api.post("/admin/mi/providers/health-check");
      toast.success("Health check complete");
      reload?.();
    } catch { toast.error("Health check failed"); }
    finally { setChecking(false); }
  };

  return (
    <StudioPage title="Provider Dashboard" question="Who is doing the reasoning?" testid="providers-dashboard"
      actions={
        <button onClick={runHealthCheck} disabled={checking} data-testid="run-health-check"
          className="flex items-center gap-2 text-sm border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold/10 transition-colors disabled:opacity-60">
          {checking ? <Loader2 className="animate-spin" size={14} /> : <Activity size={14} />} Run Health Check
        </button>
      }>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric testid="prov-active" label="Active Provider" value={titleCase(providers.active_provider) || "None"} status={providers.active_provider ? "healthy" : "error"} icon={ShieldCheck} />
        <Metric testid="prov-failover" label="Failover" value={titleCase((providers.failover_status || "").replace("_", " "))} sub={(providers.failover_order || []).map(titleCase).join(" → ")} icon={GitBranch} />
        <Metric testid="prov-latency" label="Avg Response" value={providers.avg_response_time_ms != null ? `${providers.avg_response_time_ms}ms` : "—"} sub={`${providers.total_requests_sampled || 0} sampled`} icon={Gauge} />
        <Metric testid="prov-routing" label="Routing Policy" value={titleCase(providers.routing_policy)} icon={Radio} />
      </div>

      <SectionLabel className="mb-2">Reasoning Providers</SectionLabel>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="provider-cards">
        {list.map((p) => {
          const [tone, hLabel] = HEALTH[p.health] || HEALTH.unknown;
          const dotColor = p.connected ? "bg-emerald-400 animate-pulse" : p.available ? "bg-orange-400" : "bg-slate-600";
          return (
            <Panel key={p.name} className={cx("p-5", p.available ? "border-gold/40" : "opacity-80")} data-testid={`provider-${p.name}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium capitalize flex items-center gap-2">
                  <span className={cx("w-2 h-2 rounded-full", dotColor)} />{p.name}
                </span>
                <Badge tone={tone} testid={`provider-health-${p.name}`}>{hLabel}</Badge>
              </div>
              <div className="text-xs text-slate-500 mb-2 truncate font-mono">{p.model || "—"}</div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] mb-3">
                <span className={cx("inline-flex items-center gap-1", p.api_key_present ? "text-emerald-400" : "text-slate-500")}><KeyRound size={11} /> {p.api_key_present ? "API key present" : "No key"}</span>
                {p.is_default && <span className="text-gold">· default</span>}
              </div>
              <div className="flex gap-3 text-[11px] text-slate-400 mb-3">
                <span>cost {p.traits?.cost ?? "—"}</span><span>quality {p.traits?.quality ?? "—"}</span><span>speed {p.traits?.speed ?? "—"}</span>
              </div>
              {p.calls > 0 ? (
                <div className="grid grid-cols-2 gap-y-1 text-[11px] border-t border-white/5 pt-2" data-testid={`provider-metrics-${p.name}`}>
                  <span className="text-slate-500">Avg time</span><span className="text-slate-300 text-right">{p.response_time_ms != null ? `${p.response_time_ms}ms` : "—"}</span>
                  <span className="text-slate-500">Success</span><span className={cx("text-right", p.success_rate >= 0.9 ? "text-emerald-400" : "text-orange-400")}>{p.success_rate != null ? `${Math.round(p.success_rate * 100)}%` : "—"}</span>
                  <span className="text-slate-500">Total calls</span><span className="text-slate-300 text-right">{p.calls}</span>
                  <span className="text-slate-500">Failovers</span><span className="text-slate-300 text-right">{p.failover_count || 0}</span>
                </div>
              ) : <div className="text-[11px] text-slate-600 border-t border-white/5 pt-2">Add an API key to activate — no code change required.</div>}
              <div className="text-[10px] text-slate-600 mt-2">Last health check: {p.last_health_check ? new Date(p.last_health_check).toLocaleString() : "never"}</div>
            </Panel>
          );
        })}
      </div>

      <SectionLabel className="mb-2">Request / Audit History</SectionLabel>
      <DataTable testid="provider-request-history" rows={requests || []} empty="No requests logged yet."
        columns={[
          { key: "time", label: "Time", className: "text-slate-500 text-xs", render: (r) => r.timestamp ? new Date(r.timestamp).toLocaleTimeString() : "—" },
          { key: "path", label: "Endpoint", className: "font-mono text-xs", render: (r) => `${r.method || ""} ${(r.path || "").replace("/api/mi/v1", "") || "/"}` },
          { key: "role", label: "Role", render: (r) => <span className="capitalize">{r.role || "—"}</span> },
          { key: "status", label: "Status", render: (r) => <span className={r.success ? "text-emerald-400" : "text-orange-400"}>{r.status}</span> },
          { key: "ms", label: "ms", className: "font-mono text-xs", render: (r) => r.duration_ms },
        ]} />
    </StudioPage>
  );
}
